import React, { type ComponentType, type ReactNode, useEffect, useRef, useState } from 'react';
import { traverse } from '@atlaskit/adf-utils/traverse';
import type { ADFEntity } from '@atlaskit/adf-utils/types';
import type {
	EditorActions,
	EditorProps,
	WithEditorActions as WithEditorActionsType,
} from '@atlaskit/editor-core';
import { Box, xcss } from '@atlaskit/primitives';
import SectionMessage, { SectionMessageAction } from '@atlaskit/section-message';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { IGNORE_CLICK_OUTSIDE_CLASS_NAME } from '../../../../../constants.tsx';
import { PACKAGE_NAME } from '../../../../../types.tsx';
import messages from './messages.tsx';

type Props = EditorProps & {
	EditorContext: ComponentType<{
		children: ReactNode;
	}>;
	readonly onUpdate: (adf: ADFEntity) => void;
	WithEditorActions: typeof WithEditorActionsType;
	Editor: ComponentType<EditorProps>;
	onRecalculateAnimatorHeight: () => void;
	scrollableElement: HTMLElement | null;
};

type FallbackProps = {
	errorCount: number;
	onRetry: () => void;
};

export const Fallback = ({ errorCount, onRetry }: FallbackProps) => {
	const { formatMessage } = useIntl();

	return (
		<Box xcss={errorWrapperStyles}>
			<SectionMessage
				appearance="error"
				actions={
					errorCount <= 2
						? [
								<SectionMessageAction key="reload-editor-error-action" onClick={onRetry}>
									{formatMessage(messages.editorContextErrorAction)}
								</SectionMessageAction>,
							]
						: []
				}
				title={formatMessage(messages.editorContextErrorTitle)}
			>
				{errorCount <= 2
					? formatMessage(messages.editorContextErrorMessageToReloadEditor)
					: formatMessage(messages.editorContextErrorMessageToRefreshPage)}
			</SectionMessage>
		</Box>
	);
};

const onDefaultValueChange =
	(actions: EditorActions<ADFEntity>, onUpdate?: (adf: ADFEntity) => void) => () => {
		actions.getValue().then((value) => {
			if (!value) {
				return;
			}
			const doc = traverse(value, {
				mention: (node) => {
					// Omit accessLevel as documents from the API do not include this property, and we need dirty checks to be correct
					// @ts-expect-error - TS2339 - Property 'accessLevel' does not exist on type '{ [name: string]: any; } | undefined'.
					const { accessLevel, ...attrs } = node.attrs;
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					return {
						...node,
						attrs,
					} as ADFEntity;
				},
			});

			if (onUpdate && doc) {
				onUpdate(doc);
			}
		});
	};

export const AsyncEditorView = (props: Props) => {
	const { EditorContext, WithEditorActions } = props;

	const popupMountElement: React.MutableRefObject<HTMLDivElement | null> = useRef(null);

	const [errorCount, setErrorCount] = useState(0);
	const [resetCaughtError, setResetCaughtError] = useState<Error | undefined>(undefined);

	useEffect(() => {
		// Ensure the height animator has been recalculated so the async editor doesn't overflow the container
		props.onRecalculateAnimatorHeight();

		const { body } = document;
		if (body !== null) {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			popupMountElement.current = document.createElement('div');
			popupMountElement.current.setAttribute('class', IGNORE_CLICK_OUTSIDE_CLASS_NAME);
			body.appendChild(popupMountElement.current);
		}

		return function componentWillUnmount() {
			if (body !== null && popupMountElement.current != null) {
				body.removeChild(popupMountElement.current);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const renderEditor = (actions: EditorActions<ADFEntity>) => {
		const {
			onUpdate,
			Editor,
			scrollableElement,
			taskDecisionProvider,
			onRecalculateAnimatorHeight,
			...editorProps
		} = props;

		return (
			<Editor
				allowPanel
				allowRule
				appearance="comment"
				{...editorProps}
				{...(taskDecisionProvider &&
					expValEquals(
						'jira_issue_config_fields_task_and_decision_support',
						'isTasksAndDecisionsEnabled',
						true,
					) && { allowTasksAndDecisions: true, taskDecisionProvider })}
				onChange={onDefaultValueChange(actions, onUpdate)}
				allowAnalyticsGASV3
				{...(scrollableElement
					? {
							// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
							popupsMountPoint: popupMountElement.current || document.body || undefined,
							popupsScrollableElement: scrollableElement,
						}
					: null)}
				featureFlags={{
					codeBidiWarnings: true,
					'code-bidi-warnings': true,
				}}
			/>
		);
	};

	const onError = () => {
		setErrorCount(errorCount + 1);
		setResetCaughtError(undefined);
	};

	const renderAsyncEditor = () => (
		<EditorContext>
			<WithEditorActions render={renderEditor} />
		</EditorContext>
	);

	const renderAsyncEditorWithErrorBoundary = () => (
		<JSErrorBoundary
			id="editorContext"
			packageName={PACKAGE_NAME}
			teamName="morphinecake"
			fallback={({ error }) => (
				<Fallback errorCount={errorCount} onRetry={() => setResetCaughtError(error)} />
			)}
			onError={onError}
			resetCaughtError={resetCaughtError}
		>
			{renderAsyncEditor()}
		</JSErrorBoundary>
	);

	return <>{renderAsyncEditorWithErrorBoundary()}</>;
};

const errorWrapperStyles = xcss({
	marginBottom: 'space.200',
});
